import { useState, useEffect, useRef, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowUp, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Message {
  id: string;
  text: string;
  sender: 'ai' | 'user';
  timestamp: Date;
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`;

const initialSuggestions = [
  "How does Metro City Groove work?",
  "What's in it for creators?",
  "How can venues benefit?",
  "How does the affiliate program pay?",
  "How do collaboration payments work?",
];

const LINK_RE = /\[([^\]]+)\]\((\/[^)\s]+)\)/g;

/** Public pages a signed-out visitor can open directly. */
const PUBLIC_PATHS = [
  '/pricing',
  '/events',
  '/agency-pricing',
  '/privacy-policy',
  '/terms-of-service',
  '/cookie-policy',
  '/gdpr',
  '/payments-policy',
  '/collaboration-terms',
  '/auth',
];

/**
 * Home-page visitors are not signed in, so any in-app destination is sent to
 * the auth page (remembering where they wanted to go).
 */
const resolveHomeHref = (href: string) => {
  const isPublic = PUBLIC_PATHS.some((p) => href === p || href.startsWith(`${p}/`));
  return isPublic ? href : `/auth?next=${encodeURIComponent(href)}`;
};

/** Renders assistant text: bold markers, bullets and in-app links. */
const RichText = ({ text, dark }: { text: string; dark: boolean }) => {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  LINK_RE.lastIndex = 0;

  const renderPlain = (chunk: string, keyBase: string) =>
    chunk.split('**').map((part, i) =>
      i % 2 === 1 ? (
        <strong key={`${keyBase}-${i}`} className={dark ? 'text-black font-semibold' : 'text-white font-semibold'}>
          {part}
        </strong>
      ) : (
        <Fragment key={`${keyBase}-${i}`}>{part}</Fragment>
      ),
    );

  while ((match = LINK_RE.exec(text)) !== null) {
    if (match.index > last) nodes.push(...renderPlain(text.slice(last, match.index), `t-${last}`));
    const [, label, href] = match;
    nodes.push(
      <Link
        key={`l-${match.index}`}
        to={resolveHomeHref(href)}
        className="inline-flex items-center px-2 py-0.5 mx-0.5 rounded-full bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-colors"
      >
        {label}
      </Link>,
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) nodes.push(...renderPlain(text.slice(last), `t-${last}`));

  return <>{nodes}</>;
};

const Chat = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (hasStarted && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [messages, isTyping, hasStarted]);

  /** Streams an answer from the platform assistant. */
  const askAssistant = async (question: string, history: Message[]) => {
    const replyId = `${Date.now()}-ai`;
    setIsTyping(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error(
          'Create a free account or sign in and MCG AI will answer all of this for you — head to [Sign in](/auth).',
        );
      }

      const resp = await fetch(CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          messages: [
            ...history.map((m) => ({ role: m.sender === 'ai' ? 'assistant' : 'user', content: m.text })),
            { role: 'user', content: question },
          ],
        }),
      });

      if (!resp.ok || !resp.body) {
        throw new Error(
          resp.status === 429
            ? 'A lot of people are asking right now — give it a moment and try again.'
            : resp.status === 401
              ? 'Please sign in again to keep chatting with MCG AI.'
              : 'The assistant is unavailable right now. Please try again shortly.',
        );
      }

      setIsTyping(false);
      setMessages((prev) => [...prev, { id: replyId, text: '', sender: 'ai', timestamp: new Date() }]);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let text = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx: number;
        while ((idx = buffer.indexOf('\n')) !== -1) {
          let line = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 1);
          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (!line.startsWith('data: ')) continue;
          const json = line.slice(6).trim();
          if (json === '[DONE]') continue;
          try {
            const chunk = JSON.parse(json).choices?.[0]?.delta?.content as string | undefined;
            if (chunk) {
              text += chunk;
              setMessages((prev) => prev.map((m) => (m.id === replyId ? { ...m, text } : m)));
            }
          } catch {
            /* partial chunk */
          }
        }
      }

      if (!text) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === replyId ? { ...m, text: 'No answer came back — please ask that again.' } : m,
          ),
        );
      }
    } catch (e) {
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== replyId),
        {
          id: `${Date.now()}-err`,
          text: e instanceof Error ? e.message : 'Something went wrong. Please try again.',
          sender: 'ai',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const send = (value: string) => {
    const question = value.trim();
    if (!question || isTyping) return;
    if (!hasStarted) setHasStarted(true);
    const userMessage: Message = { id: `${Date.now()}-u`, text: question, sender: 'user', timestamp: new Date() };
    const history = messages;
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    void askAssistant(question, history);
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    send(input);
  };

  const formatTime = (date: Date) =>
    date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();

  // Initial state - centered input with suggestions
  if (!hasStarted) {
    return (
      <div className="w-full flex flex-col items-center justify-center flex-1 py-12 animate-fade-in">
        <div className="w-full max-w-2xl">
          <form onSubmit={handleSendMessage} className="relative">
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 rounded-full opacity-50 group-hover:opacity-75 blur transition-opacity"></div>
              <div className="relative">
                <Input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about Metro City Groove..."
                  className="w-full h-14 bg-black/80 border border-gray-800 text-white placeholder:text-gray-500 rounded-full px-6 pr-14 text-base focus:ring-1 focus:ring-gray-600 focus:border-gray-600 transition-all backdrop-blur-sm"
                />
                <Button
                  type="submit"
                  disabled={input.trim() === ''}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white hover:bg-gray-200 text-black rounded-full p-0 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ArrowUp className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </form>

          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {initialSuggestions.map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => send(suggestion)}
                className="px-4 py-2 text-sm rounded-full bg-transparent text-gray-400 border border-gray-800 hover:border-gray-600 hover:text-white hover:bg-gray-900/50 transition-all"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Active chat state
  return (
    <div className="w-full flex flex-col flex-1 animate-fade-in transition-all duration-500 ease-out">
      <div className="flex-1 overflow-y-auto max-h-[400px] mb-4 scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        <div className="space-y-4 py-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}
            >
              <div className={`max-w-[85%] ${message.sender === 'user' ? 'ml-auto' : ''}`}>
                <div className="flex items-center gap-2 mb-1">
                  {message.sender === 'ai' && (
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center border border-gray-700">
                      <Sparkles className="w-3 h-3 text-gray-300" />
                    </div>
                  )}
                  <span className="text-xs text-gray-500">
                    {message.sender === 'ai' ? 'Assistant' : 'You'} · {formatTime(message.timestamp)}
                  </span>
                </div>
                <div
                  className={`rounded-2xl px-4 py-3 ${
                    message.sender === 'user'
                      ? 'bg-white text-black rounded-tr-sm'
                      : 'bg-gray-900/80 text-gray-100 rounded-tl-sm border border-gray-800 backdrop-blur-sm'
                  }`}
                >
                  <div className="whitespace-pre-line text-sm leading-relaxed">
                    <RichText text={message.text} dark={message.sender === 'user'} />
                  </div>
                </div>
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex justify-start animate-fade-in">
              <div className="max-w-[85%]">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center border border-gray-700">
                    <Sparkles className="w-3 h-3 text-gray-300" />
                  </div>
                  <span className="text-xs text-gray-500">Assistant</span>
                </div>
                <div className="bg-gray-900/80 text-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 border border-gray-800 backdrop-blur-sm">
                  <div className="flex items-center space-x-1.5">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="mt-auto">
        <form onSubmit={handleSendMessage} className="relative">
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 rounded-full opacity-30 group-hover:opacity-50 blur transition-opacity"></div>
            <div className="relative">
              <Input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask a follow-up question..."
                className="w-full h-12 bg-black/80 border border-gray-800 text-white placeholder:text-gray-500 rounded-full px-5 pr-12 text-sm focus:ring-1 focus:ring-gray-600 focus:border-gray-600 transition-all backdrop-blur-sm"
              />
              <Button
                type="submit"
                disabled={input.trim() === '' || isTyping}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 bg-white hover:bg-gray-200 text-black rounded-full p-0 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ArrowUp className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </form>

        <div className="mt-3 flex flex-wrap justify-center gap-2">
          {initialSuggestions.slice(0, 3).map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => send(suggestion)}
              className="px-3 py-1.5 text-xs rounded-full bg-transparent text-gray-500 border border-gray-800 hover:border-gray-600 hover:text-gray-300 transition-all"
            >
              {suggestion}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Chat;
